/* =====================================================================
 * StoragePage — Finder형 파일 저장소
 * - 직접 업로드/생성 불가
 * - 워크스페이스 작업으로 생성/업로드된 파일만 노출
 * ===================================================================== */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import styled from 'styled-components';
import { Download, Menu, PanelLeft, Trash2, Sparkles, FileText, X } from 'lucide-react';
import { useSkillStore } from '@auw/stores';
import { Chip } from '@auw/workspace-ui';
import * as workspaceApi from '@auw/api-client';
import { authFetch } from '@auw/api-client';
import type { WorkspaceFile, WorkspaceItem, WorkspaceMode } from '@auw/workspace-utils';
import { useChatStore } from '@auw/stores';
import { useTeamStore } from '@auw/stores';
import { useWorkspaceNavigationStore } from '@auw/stores';
import { conversationToItem } from '@auw/api-client';
import { onAppEvent } from '@auw/stores';
import { useFeatureFlags } from '@auw/feature-flags';
import { WORKSPACE_MODES, WORKSPACE_ORDER, modeToPathWithId } from '@auw/workspace-utils';
import { DropdownMenu, type DropdownMenuItem } from '@auw/workspace-ui';
import { getSpecializedStorageComponents } from '../../lib/specializedStorageSlot';
import { StorageSidebar, type SidebarSelection } from './StorageSidebar';
import { StorageToolbar, type BreadcrumbEntry } from './StorageToolbar';
import { StorageFileList } from './StorageFileList';
import { StorageEmptyState } from './StorageEmptyState';
import { StorageContainer, SlideTrack, MainPane } from './storage.styles';

/* P11-5f: stable empty-set reference passed to <StorageFileList> when
 * `mode.storage.multiSelect` is disabled. Using a sentinel keeps the
 * downstream memoization stable. */
const EMPTY_SELECTION: ReadonlySet<string> = new Set<string>();
const DOC_AGENT_DOCUMENT_EXTENSIONS = new Set(['docx', 'xlsx', 'hwpx']);

/* ------------------------------------------------------------------ */
/* Page-level wrapper & main sidebar expand button                     */
/* ------------------------------------------------------------------ */

const PageWrap = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  min-height: 0;
`;

const MainSidebarExpandBtn = styled.button`
  position: absolute;
  top: 8px;
  left: 16px;
  width: 36px;
  height: 36px;
  border: none;
  border-radius: ${({ theme }) => theme.sizes.borderRadiusSm};
  background: transparent;
  color: ${({ theme }) => theme.colors.textSecondary};
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  z-index: 10;

  &:hover {
    background: ${({ theme }) => theme.colors.bgHover};
    color: ${({ theme }) => theme.colors.textPrimary};
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    display: none;
  }
`;

/* ------------------------------------------------------------------ */
/* Collapsed sidebar toggle (matches WorkspaceModeLayout)              */
/* ------------------------------------------------------------------ */

const CollapsedSidebarToggleSlot = styled.div`
  position: absolute;
  left: 0;
  top: 0;
  min-height: ${({ theme }) => theme.sidebar.headerHeight};
  width: 44px;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 5;

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    display: none;
  }
`;

const CollapsedSidebarToggleBtn = styled.button`
  width: 36px;
  height: 36px;
  border: 1px solid ${({ theme }) =>
    theme.mode === 'dark' ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.08)'};
  border-radius: 999px;
  background: ${({ theme }) =>
    theme.mode === 'dark' ? 'rgba(33,33,33,0.72)' : 'rgba(255,255,255,0.76)'};
  color: ${({ theme }) => theme.colors.textSecondary};
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  pointer-events: auto;
  box-shadow: ${({ theme }) =>
    theme.mode === 'dark'
      ? '0 8px 18px rgba(0,0,0,0.24)'
      : '0 8px 18px rgba(0,0,0,0.08)'};
  backdrop-filter: blur(14px) saturate(160%);
  -webkit-backdrop-filter: blur(14px) saturate(160%);
  transition:
    background ${({ theme }) => theme.transitions.fast},
    color ${({ theme }) => theme.transitions.fast},
    border-color ${({ theme }) => theme.transitions.fast},
    box-shadow ${({ theme }) => theme.transitions.fast};

  &:hover {
    background: ${({ theme }) =>
      theme.mode === 'dark' ? 'rgba(58,58,58,0.86)' : 'rgba(255,255,255,0.92)'};
    color: ${({ theme }) => theme.colors.textPrimary};
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.accent};
    outline-offset: 2px;
  }
`;

/* ------------------------------------------------------------------ */
/* Debounce hook                                                       */
/* ------------------------------------------------------------------ */

function useDebounce<T>(value: T, delay: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(timer);
  }, [value, delay]);
  return debounced;
}

function getSourceItemId(file: WorkspaceFile): string | null {
  const direct = file.source_item_id;
  if (typeof direct === 'string' && direct.trim()) return direct;
  const metaItemId = (file.metadata as Record<string, unknown> | undefined)?.item_id;
  if (typeof metaItemId === 'string' && metaItemId.trim()) return metaItemId;
  return null;
}

function getLowerExtension(name: string | null | undefined): string | null {
  const ext = (name ?? '').split('.').pop()?.toLowerCase() ?? '';
  return ext || null;
}

function getDocAgentDocumentFormat(file: WorkspaceFile): string | null {
  const meta = (file.metadata as Record<string, unknown> | undefined) ?? {};
  const metaFormat = meta.doc_agent_format;
  if (typeof metaFormat === 'string' && DOC_AGENT_DOCUMENT_EXTENSIONS.has(metaFormat)) {
    return metaFormat;
  }
  const ext = getLowerExtension(file.original_name);
  return ext && DOC_AGENT_DOCUMENT_EXTENSIONS.has(ext) ? ext : null;
}

function isDocAgentDocumentFile(file: WorkspaceFile): boolean {
  const format = getDocAgentDocumentFormat(file);
  if (!format) return false;
  const sourceType = (file.source_type ?? '').toLowerCase();
  return (
    getWorkspaceType(file) === 'doc-agent'
    || sourceType.startsWith('doc-agent')
    || sourceType.startsWith('doc_agent')
    || typeof (file.metadata as Record<string, unknown> | undefined)?.doc_agent_format === 'string'
  );
}

function toDocAgentFileName(title: string, format: string): string {
  const trimmed = title.trim();
  const ext = `.${format}`;
  if (!trimmed) return `document${ext}`;
  return trimmed.toLowerCase().endsWith(ext) ? trimmed : `${trimmed}${ext}`;
}

function withDocAgentSourceTitles(
  files: WorkspaceFile[],
  titleByItemId: Map<string, string>,
): WorkspaceFile[] {
  if (titleByItemId.size === 0) return files;
  return files.map((file) => {
    if (!isDocAgentDocumentFile(file)) return file;
    const sourceItemId = getSourceItemId(file);
    if (!sourceItemId) return file;
    const syncedName = titleByItemId.get(sourceItemId);
    if (!syncedName || syncedName === file.original_name) return file;
    return { ...file, original_name: syncedName };
  });
}

function dedupDocAgentFilesByItem(files: WorkspaceFile[]): WorkspaceFile[] {
  const byItem = new Map<string, WorkspaceFile>();
  const passthrough: WorkspaceFile[] = [];

  for (const file of files) {
    const sourceItemId = getSourceItemId(file);
    if (!sourceItemId || !isDocAgentDocumentFile(file)) {
      passthrough.push(file);
      continue;
    }

    const prev = byItem.get(sourceItemId);
    if (!prev || String(file.created_at ?? '') > String(prev.created_at ?? '')) {
      byItem.set(sourceItemId, file);
    }
  }

  return [...byItem.values(), ...passthrough];
}

function getWorkspaceType(file: WorkspaceFile): WorkspaceMode | null {
  const raw = (file.metadata as Record<string, unknown> | undefined)?.workspace_type;
  if (typeof raw !== 'string' || !raw.trim()) return null;
  return raw as WorkspaceMode;
}

/**
 * source_type 문자열에서 WorkspaceMode 를 유추한다.
 * metadata.workspace_type 이 비어 있는 레거시 파일 대비 fallback.
 *
 * 주의: messaging-bridge(Slack/Discord/external) 업로드는 백엔드에서
 * workspace_type='agent' 로 저장되므로, source_type 만 보면 'message' 로
 * 보일 수 있어도 실제 라우트는 'agent' 가 맞다. 따라서 metadata 없는
 * 레거시 파일에 대해서도 동일하게 'agent' 로 추론한다.
 */
function inferModeFromSourceType(sourceType: string): WorkspaceMode | null {
  const st = (sourceType ?? '').toLowerCase();
  if (!st) return null;
  if (st === 'dm_upload' || st.startsWith('dm_')) return 'message';
  if (st.startsWith('doc-agent') || st.startsWith('doc_agent')) return 'doc-agent';
  if (st.startsWith('agent_')) return 'agent';
  // slack_upload / discord_upload / external_upload 등 messaging-bridge
  // 업로드도 agent 워크스페이스로 라우트한다 (백엔드와 일치).
  if (st.endsWith('_upload') || st.endsWith('_output')) return 'agent';
  return null;
}

/**
 * 파일의 "출처" 바로가기 타깃 (라우트 + 메시지) 을 결정한다.
 * source_item_id (또는 metadata.item_id) 가 없으면 null → 아이콘 비활성.
 */
function resolveSourceTarget(file: WorkspaceFile): {
  mode: WorkspaceMode;
  itemId: string;
  messageId?: string;
} | null {
  const itemId = getSourceItemId(file);
  if (!itemId) return null;

  const mode = getWorkspaceType(file) ?? inferModeFromSourceType(file.source_type ?? '');
  if (!mode) return null;

  const meta = (file.metadata as Record<string, unknown> | undefined) ?? {};
  const rawMsg = meta.message_id;
  const messageId = typeof rawMsg === 'string' && rawMsg.trim() ? rawMsg : undefined;

  return { mode, itemId, messageId };
}

function filterBySelection(files: WorkspaceFile[], itemIds: Set<string>, mode: WorkspaceMode | null): WorkspaceFile[] {
  if (itemIds.size === 0) return [];
  return files.filter((file) => {
    const sourceItemId = getSourceItemId(file);
    if (sourceItemId && itemIds.has(sourceItemId)) return true;
    if (!mode) return false;
    return getWorkspaceType(file) === mode;
  });
}

function sortByUpdatedDesc(items: WorkspaceItem[]): WorkspaceItem[] {
  return [...items].sort((a, b) => {
    const diff = new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime();
    if (diff !== 0) return diff;
    return a.title.localeCompare(b.title);
  });
}

const FINDER_MODES: WorkspaceMode[] = WORKSPACE_ORDER;
const FINDER_MODE_SET = new Set<WorkspaceMode>(FINDER_MODES);

/* ------------------------------------------------------------------ */
/* Component                                                           */
/* ------------------------------------------------------------------ */

export const StoragePage: React.FC = () => {
  const { t } = useTranslation('common');
  const addToast = useChatStore((s) => s.addToast);
  const conversations = useChatStore((s) => s.conversations);
  const mainSidebarOpen = useChatStore((s) => s.sidebarOpen);
  const setMainSidebarOpen = useChatStore((s) => s.setSidebarOpen);
  const teams = useTeamStore((s) => s.teams);
  const teamsLoaded = useTeamStore((s) => s.isLoaded);
  const loadMyTeams = useTeamStore((s) => s.loadMyTeams);
  const workspaceView = useTeamStore((s) => s.workspaceView);
  const pageSidebarOpen = useWorkspaceNavigationStore((s) => s.isPageSidebarOpen('storage'));
  const toggleStoredPageSidebar = useWorkspaceNavigationStore((s) => s.togglePageSidebar);

  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const [mobileShowContent, setMobileShowContent] = useState(false);
  const togglePageSidebar = useCallback(() => {
    if (window.matchMedia('(max-width: 768px)').matches) {
      setMobileShowContent((prev) => !prev);
      return;
    }
    toggleStoredPageSidebar('storage');
  }, [toggleStoredPageSidebar]);

  const [selection, setSelection] = useState<SidebarSelection>(() => (
    workspaceView === 'personal'
      ? { section: 'workspace', teamId: null, mode: 'agent' }
      : { section: 'team-space', teamId: workspaceView, mode: 'agent' }
  ));

  const [searchQuery, setSearchQuery] = useState('');
  const debouncedSearch = useDebounce(searchQuery, 250);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  /* P11-5f: when `mode.storage.multiSelect` is off the bulk selection
   * UI (selection bar, checkbox column on the file list, ctx-menu
   * "Select N items" actions) is suppressed and only single-row
   * actions remain available. */
  const multiSelectEnabled = useFeatureFlags().isEnabled('mode.storage.multiSelect');

  const [ctxMenu, setCtxMenu] = useState<{
    open: boolean;
    x: number;
    y: number;
    fileId: string;
  }>({ open: false, x: 0, y: 0, fileId: '' });

  useEffect(() => {
    if (!teamsLoaded) void loadMyTeams();
  }, [teamsLoaded, loadMyTeams]);

  useEffect(() => {
    return onAppEvent('workspace:togglePageSidebar', () => {
      togglePageSidebar();
    });
  }, [togglePageSidebar]);

  const allItems = useMemo<WorkspaceItem[]>(
    () =>
      Object.values(conversations)
        .map(conversationToItem)
        .filter((item) => item.kind === 'page' && FINDER_MODE_SET.has(item.mode as WorkspaceMode)),
    [conversations],
  );

  const workspaceItems = useMemo(
    () => sortByUpdatedDesc(allItems.filter((item) => item.scope === 'personal')),
    [allItems],
  );

  const teamWorkspaceItems = useMemo<Record<string, WorkspaceItem[]>>(() => {
    const grouped: Record<string, WorkspaceItem[]> = {};
    for (const item of allItems) {
      if (item.scope !== 'team') continue;
      if (!item.team_id) continue;
      if (!grouped[item.team_id]) grouped[item.team_id] = [];
      grouped[item.team_id].push(item);
    }
    for (const teamId of Object.keys(grouped)) {
      grouped[teamId] = sortByUpdatedDesc(grouped[teamId]);
    }
    return grouped;
  }, [allItems]);

  const workspaceModeItemIds = useMemo<Record<string, Set<string>>>(() => {
    const map: Record<string, Set<string>> = {};
    for (const mode of FINDER_MODES) map[mode] = new Set<string>();
    for (const item of workspaceItems) {
      map[item.mode]?.add(item.item_id);
    }
    return map;
  }, [workspaceItems]);

  const teamModeItemIds = useMemo<Record<string, Record<string, Set<string>>>>(() => {
    const out: Record<string, Record<string, Set<string>>> = {};
    for (const [teamId, items] of Object.entries(teamWorkspaceItems)) {
      const modeMap: Record<string, Set<string>> = {};
      for (const mode of FINDER_MODES) modeMap[mode] = new Set<string>();
      for (const item of items) {
        modeMap[item.mode]?.add(item.item_id);
      }
      out[teamId] = modeMap;
    }
    return out;
  }, [teamWorkspaceItems]);

  const selectionItemIds = useMemo(() => {
    const ids = new Set<string>();
    if (selection.section === 'direct-message') {
      return ids;
    }
    if (selection.section === 'workspace') {
      if (selection.mode) {
        for (const id of workspaceModeItemIds[selection.mode] ?? []) ids.add(id);
      } else {
        for (const mode of FINDER_MODES) {
          for (const id of workspaceModeItemIds[mode] ?? []) ids.add(id);
        }
      }
      return ids;
    }

    const teamId = selection.teamId ?? teams[0]?.team_id ?? null;
    if (!teamId) return ids;
    const modeMap = teamModeItemIds[teamId] ?? {};
    if (selection.mode) {
      for (const id of modeMap[selection.mode] ?? []) ids.add(id);
    } else {
      for (const mode of FINDER_MODES) {
        for (const id of modeMap[mode] ?? []) ids.add(id);
      }
    }
    return ids;
  }, [selection, teams, workspaceModeItemIds, teamModeItemIds]);

  const selectionItemIdsKey = useMemo(
    () => [...selectionItemIds].sort().join(','),
    [selectionItemIds],
  );

  const selectionKey = `${selection.section}:${selection.teamId ?? 'none'}:${selection.mode ?? 'all'}`;

  useEffect(() => {
    if (selection.section !== 'team-space' && selection.section !== 'direct-message') return;
    if (teams.length === 0) return;
    const hasTeam = selection.teamId && teams.some((team) => team.team_id === selection.teamId);
    if (hasTeam) return;
    setSelection((prev) => ({
      section: prev.section,
      teamId: teams[0].team_id,
      mode: prev.section === 'direct-message' ? null : prev.mode ?? 'agent',
    }));
  }, [selection.section, selection.teamId, teams]);

  const filesQuery = useQuery<WorkspaceFile[]>({
    queryKey: [
      'storage',
      'finder-files',
      selectionKey,
      selectionItemIdsKey,
    ],
    queryFn: async () => {
      if (selection.section === 'direct-message') {
        const teamId = selection.teamId ?? teams[0]?.team_id ?? null;
        if (!teamId) return [];
        const { files } = await workspaceApi.listFiles({
          scope: 'team',
          team_id: teamId,
          source_type: 'dm_upload',
          limit: 500,
        });
        return files;
      }
      if (selectionItemIds.size === 0) return [];
      if (selection.section === 'team-space') {
        const teamId = selection.teamId ?? teams[0]?.team_id ?? null;
        if (!teamId) return [];
        const { files } = await workspaceApi.listFiles({
          scope: 'team',
          team_id: teamId,
          limit: 500,
        });
        return filterBySelection(files, selectionItemIds, selection.mode);
      }
      const { files } = await workspaceApi.listFiles({
        scope: 'personal',
        limit: 500,
      });
      return filterBySelection(files, selectionItemIds, selection.mode);
    },
  });

  const refreshFinderFiles = useCallback(async () => {
    await queryClient.invalidateQueries({ queryKey: ['storage', 'finder-files'] });
    await queryClient.invalidateQueries({ queryKey: ['workspace', 'files'] });
  }, [queryClient]);

  useEffect(() => {
    return onAppEvent('workspace:itemsChanged', () => {
      void refreshFinderFiles();
    });
  }, [refreshFinderFiles]);

  useEffect(() => {
    return onAppEvent('storage:agentFilesCreated', () => {
      setTimeout(() => {
        void refreshFinderFiles();
      }, 700);
    });
  }, [refreshFinderFiles]);

  useEffect(() => {
    setSelectedIds(new Set());
    setSearchQuery('');
  }, [selectionKey]);

  const docAgentFileNameByItemId = useMemo(() => {
    const map = new Map<string, string>();
    for (const conversation of Object.values(conversations)) {
      if (conversation.mode !== 'doc-agent') continue;
      const settings = conversation.workspaceSettings ?? {};
      const rawFormat = settings.docAgentFormat;
      const format = typeof rawFormat === 'string' && DOC_AGENT_DOCUMENT_EXTENSIONS.has(rawFormat)
        ? rawFormat
        : 'docx';
      if (conversation.title.trim()) {
        map.set(conversation.id, toDocAgentFileName(conversation.title, format));
      }
    }
    return map;
  }, [conversations]);

  const files = useMemo(() => {
    const withTitles = withDocAgentSourceTitles(
      filesQuery.data ?? [],
      docAgentFileNameByItemId,
    );
    return dedupDocAgentFilesByItem(withTitles);
  }, [docAgentFileNameByItemId, filesQuery.data]);

  const filteredFiles = useMemo(() => {
    const query = debouncedSearch.trim().toLowerCase();
    if (!query) return files;
    return files.filter((file) => file.original_name.toLowerCase().includes(query));
  }, [files, debouncedSearch]);

  const allSelected = filteredFiles.length > 0
    && filteredFiles.every((file) => selectedIds.has(file.file_id));

  const toggleSelect = useCallback((id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const handleSelectAll = useCallback(() => {
    if (allSelected) {
      setSelectedIds(new Set());
      return;
    }
    setSelectedIds(new Set(filteredFiles.map((file) => file.file_id)));
  }, [allSelected, filteredFiles]);

  const handleDeleteFiles = useCallback(async (fileIds: string[]) => {
    const uniqueIds = Array.from(new Set(fileIds.filter(Boolean)));
    if (uniqueIds.length === 0) return;

    const results = await Promise.allSettled(
      uniqueIds.map((id) => workspaceApi.deleteFile(id)),
    );
    const successCount = results.filter((r) => r.status === 'fulfilled').length;
    const failCount = uniqueIds.length - successCount;

    if (successCount > 0) {
      addToast({
        type: failCount === 0 ? 'success' : 'warning',
        title:
          successCount === 1
            ? t('storage.deleteSuccess')
            : t('storage.deleteSuccessMany', { count: successCount }),
      });
    }
    if (failCount > 0) {
      addToast({
        type: 'error',
        title:
          failCount === uniqueIds.length
            ? t('storage.deleteFail')
            : t('storage.deleteFailMany', { count: failCount }),
      });
    }

    setSelectedIds((prev) => {
      const next = new Set(prev);
      for (const id of uniqueIds) next.delete(id);
      return next;
    });
    await refreshFinderFiles();
  }, [addToast, refreshFinderFiles, t]);

  const fileById = useMemo(
    () => new Map(files.map((file) => [file.file_id, file])),
    [files],
  );

  const handleDownloadFile = useCallback(async (fileId: string, fallbackName?: string) => {
    try {
      const downloadName = fallbackName || fileById.get(fileId)?.original_name || 'download';
      const signed = await workspaceApi.getFileSignedUrl(fileId, 'download');
      if (signed.url) {
        const a = document.createElement('a');
        a.href = signed.url;
        a.download = downloadName;
        a.rel = 'noopener';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        return;
      }

      const res = await authFetch(workspaceApi.getFileDownloadUrl(fileId));
      if (!res.ok) {
        throw new Error(`download ${res.status}`);
      }

      const blob = await res.blob();
      if (!blob || blob.size === 0) throw new Error('Empty response');

      const blobUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = downloadName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);
    } catch {
      addToast({ type: 'error', title: t('storage.downloadFail') });
    }
  }, [addToast, fileById]);

  const handleOpenFile = useCallback((file: WorkspaceFile) => {
    void handleDownloadFile(file.file_id, file.original_name);
  }, [handleDownloadFile]);

  /**
   * "출처" 컬럼 바로가기 클릭 → 해당 파일이 업로드/생성된 워크스페이스 페이지로
   * 라우트 이동하고, message_id 가 있으면 URL 해시(#m-{id}) 로 전달해 채팅 영역의
   * useScrollToHashMessage 훅이 해당 버블로 스크롤하도록 한다.
   */
  const handleOpenSource = useCallback((file: WorkspaceFile) => {
    const target = resolveSourceTarget(file);
    if (!target) return;
    const path = modeToPathWithId(target.mode, target.itemId);
    const url = target.messageId ? `${path}#m-${target.messageId}` : path;
    navigate(url);
  }, [navigate]);

  const canOpenSource = useCallback((file: WorkspaceFile) => {
    return resolveSourceTarget(file) !== null;
  }, []);

  const handleContextMenu = useCallback((e: React.MouseEvent, fileId: string) => {
    setCtxMenu({
      open: true,
      x: e.clientX,
      y: e.clientY,
      fileId,
    });
  }, []);

  const ctxItems = useMemo<DropdownMenuItem[]>(() => {
    if (!ctxMenu.open) return [];
    const targetId = ctxMenu.fileId;
    /* P11-5f: collapse the "act on N selected" affordance when multi-select
     * is disabled — the ctx-menu only ever operates on the single row. */
    const useSelection = multiSelectEnabled && selectedIds.has(targetId) && selectedIds.size > 1;
    const targetIds = useSelection ? Array.from(selectedIds) : [targetId];

    const downloadLabel =
      targetIds.length > 1
        ? t('storage.downloadSelectedCount', { count: targetIds.length })
        : t('storage.download');
    const deleteLabel =
      targetIds.length > 1
        ? t('storage.deleteSelectedCount', { count: targetIds.length })
        : t('button.delete');

    return [
      {
        id: 'download',
        label: downloadLabel,
        icon: <Download size={14} />,
        onClick: () => {
          for (const id of targetIds) {
            void handleDownloadFile(id, fileById.get(id)?.original_name);
          }
        },
      },
      {
        id: 'delete',
        label: deleteLabel,
        icon: <Trash2 size={14} />,
        danger: true,
        onClick: () => void handleDeleteFiles(targetIds),
      },
    ];
  }, [ctxMenu, fileById, handleDeleteFiles, handleDownloadFile, multiSelectEnabled, selectedIds, t]);

  const breadcrumbs = useMemo<BreadcrumbEntry[]>(() => {
    const sectionLabel =
      selection.section === 'team-space'
        ? t('storage.teamSpace')
        : selection.section === 'direct-message'
          ? t('storage.directMessage')
        : t('sidebar.workspace');

    const out: BreadcrumbEntry[] = [
      { id: `section:${selection.section}`, label: sectionLabel },
    ];

    if ((selection.section === 'team-space' || selection.section === 'direct-message') && selection.teamId) {
      const teamName = teams.find((team) => team.team_id === selection.teamId)?.name ?? t('sidebar.team');
      out.push({ id: `team:${selection.teamId}`, label: teamName });
    }

    if (selection.section !== 'direct-message' && selection.mode) {
      out.push({
        id: `mode:${selection.mode}`,
        label: t(WORKSPACE_MODES[selection.mode].labelKey),
      });
    }
    return out;
  }, [selection, teams, t]);

  const handleBreadcrumbNav = useCallback((id: string) => {
    if (id.startsWith('section:')) {
      const section = id.replace('section:', '') as SidebarSelection['section'];
      if (section === 'team-space') {
        setSelection({
          section,
          teamId: selection.teamId ?? teams[0]?.team_id ?? null,
          mode: selection.mode ?? 'agent',
        });
        return;
      }
      if (section === 'direct-message') {
        setSelection({
          section,
          teamId: selection.teamId ?? teams[0]?.team_id ?? null,
          mode: null,
        });
        return;
      }
      setSelection({
        section,
        teamId: null,
        mode: selection.mode ?? 'agent',
      });
      return;
    }

    if (id.startsWith('team:')) {
      const teamId = id.replace('team:', '') || null;
      setSelection({
        section: selection.section === 'direct-message' ? 'direct-message' : 'team-space',
        teamId,
        mode: selection.section === 'direct-message' ? null : selection.mode ?? 'agent',
      });
      return;
    }

    if (id.startsWith('mode:')) {
      const mode = id.replace('mode:', '') as WorkspaceMode;
      setSelection((prev) => ({ ...prev, mode }));
    }
  }, [selection.mode, selection.teamId, teams]);

  const handleSidebarSelect = useCallback((sel: SidebarSelection) => {
    setSelection(sel);
    if (sel.mode || sel.section === 'direct-message') setMobileShowContent(true);
  }, []);

  const handleMobileBack = useCallback(() => {
    setMobileShowContent(false);
  }, []);

  const isSearchEmpty = filteredFiles.length === 0 && debouncedSearch.trim().length > 0;
  const isEmpty = filteredFiles.length === 0 && debouncedSearch.trim().length === 0;

  return (
    <PageWrap>
      {!mainSidebarOpen && (
        <MainSidebarExpandBtn
          type="button"
          onClick={() => setMainSidebarOpen(true)}
          title={t('storage.openSidebar')}
        >
          <PanelLeft size={18} />
        </MainSidebarExpandBtn>
      )}
      <StorageContainer>
        <SlideTrack $showContent={mobileShowContent}>
          <StorageSidebar
          teams={teams}
          teamWorkspaceItems={teamWorkspaceItems}
          workspaceItems={workspaceItems}
          selection={selection}
          onSelect={handleSidebarSelect}
          pageSidebarOpen={pageSidebarOpen}
          onTogglePageSidebar={togglePageSidebar}
          desktopHidden={!pageSidebarOpen}
        />

        <MainPane>
          {!pageSidebarOpen && (
            <CollapsedSidebarToggleSlot>
              <CollapsedSidebarToggleBtn
                type="button"
                onClick={togglePageSidebar}
                title={t('storage.expandSidebar')}
              >
                <Menu size={16} />
              </CollapsedSidebarToggleBtn>
            </CollapsedSidebarToggleSlot>
          )}

          <StorageToolbar
            breadcrumbs={breadcrumbs}
            searchQuery={searchQuery}
            fileCount={filteredFiles.length}
            sidebarCollapsed={!pageSidebarOpen}
            onMobileBack={handleMobileBack}
            onNavigate={handleBreadcrumbNav}
            onSearchChange={setSearchQuery}
          />

          {multiSelectEnabled && selection.section !== 'skills' && selectedIds.size > 0 && (
            <SelectionBar>
              <SelectionInfo>
                <SelectionCount>
                  {t('storage.selectedCount', { count: selectedIds.size })}
                </SelectionCount>
                <ClearSelectionBtn
                  type="button"
                  onClick={() => setSelectedIds(new Set())}
                  title={t('storage.clearSelection')}
                >
                  <X size={14} />
                </ClearSelectionBtn>
              </SelectionInfo>
              <SelectionActions>
                <SelectionActionBtn
                  type="button"
                  onClick={() => {
                    for (const id of Array.from(selectedIds)) {
                      void handleDownloadFile(
                        id,
                        fileById.get(id)?.original_name,
                      );
                    }
                  }}
                >
                  <Download size={14} />
                  <span>{t('storage.download')}</span>
                </SelectionActionBtn>
                <SelectionActionBtn
                  type="button"
                  $danger
                  onClick={() => void handleDeleteFiles(Array.from(selectedIds))}
                >
                  <Trash2 size={14} />
                  <span>{t('button.delete')}</span>
                </SelectionActionBtn>
              </SelectionActions>
            </SelectionBar>
          )}

          {(() => {
            // specialized 에이전트(cad/estimate) 또는 슬롯을 등록한 개인
            // workspace 모드(doc-agent) 면 등록된 계층 List 를 렌더한다.
            const slotId =
              selection.section === 'specialized'
                ? selection.specializedAgentId ?? null
                : selection.section === 'workspace'
                  ? selection.mode ?? null
                  : null;
            return slotId ? getSpecializedStorageComponents(slotId) : null;
          })() ? (
            (() => {
              const slotId =
                selection.section === 'specialized'
                  ? selection.specializedAgentId ?? null
                  : selection.mode ?? null;
              const comps = slotId
                ? getSpecializedStorageComponents(slotId)
                : null;
              if (comps) {
                const List = comps.List;
                return <List selection={selection} onSelect={setSelection} />;
              }
              return null;
            })()
          ) : selection.section === 'specialized' ? (
            <LoadingWrap>
              <LoadingLabel>준비 중입니다.</LoadingLabel>
            </LoadingWrap>
          ) : selection.section === 'skills' ? (
            <SkillsSection />
          ) : (
            <>
              {filesQuery.isLoading && (
                <LoadingWrap>
                  <LoadingLabel>{t('storage.loading')}</LoadingLabel>
                </LoadingWrap>
              )}

              {!filesQuery.isLoading && (isSearchEmpty || isEmpty) && (
                <StorageEmptyState
                  isSearchEmpty={isSearchEmpty}
                  searchQuery={debouncedSearch}
                />
              )}

              {!filesQuery.isLoading && filteredFiles.length > 0 && (
                <StorageFileList
                  files={filteredFiles}
                  selectedIds={multiSelectEnabled ? selectedIds : (EMPTY_SELECTION as Set<string>)}
                  onToggleSelect={multiSelectEnabled ? toggleSelect : undefined}
                  onSelectAll={multiSelectEnabled ? handleSelectAll : undefined}
                  allSelected={multiSelectEnabled ? allSelected : false}
                  onOpenFile={handleOpenFile}
                  onContextMenu={handleContextMenu}
                  onOpenSource={handleOpenSource}
                  canOpenSource={canOpenSource}
                />
              )}
            </>
          )}
        </MainPane>
      </SlideTrack>

      <DropdownMenu
        open={ctxMenu.open}
        x={ctxMenu.x}
        y={ctxMenu.y}
        items={ctxItems}
        onClose={() => setCtxMenu((prev) => ({ ...prev, open: false }))}
      />
    </StorageContainer>
    </PageWrap>
  );
};

const LoadingWrap = styled.div`
  flex: 1;
  min-height: 0;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const LoadingLabel = styled.span`
  font-size: 0.875rem;
  color: ${({ theme }) => theme.colors.textMuted};
`;

const SelectionBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 8px 20px;
  flex-shrink: 0;
  border-bottom: 0.5px solid
    ${({ theme }) =>
      theme.mode === 'dark' ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.05)'};
  background: ${({ theme }) =>
    theme.mode === 'dark'
      ? 'rgba(255,149,0,0.08)'
      : 'rgba(255,149,0,0.06)'};
  backdrop-filter: blur(12px) saturate(160%);
  -webkit-backdrop-filter: blur(12px) saturate(160%);
`;

const SelectionInfo = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  min-width: 0;
`;

const SelectionCount = styled.span`
  font-size: 0.75rem;
  font-weight: 600;
  letter-spacing: -0.01em;
  color: ${({ theme }) => theme.colors.accent};
  white-space: nowrap;
`;

const ClearSelectionBtn = styled.button`
  width: 20px;
  height: 20px;
  border: none;
  border-radius: 999px;
  background: transparent;
  color: ${({ theme }) => theme.colors.textMuted};
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all ${({ theme }) => theme.transitions.fast};

  &:hover {
    background: ${({ theme }) =>
      theme.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)'};
    color: ${({ theme }) => theme.colors.textPrimary};
  }
`;

const SelectionActions = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
`;

const SelectionActionBtn = styled.button<{ $danger?: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 26px;
  padding: 0 12px;
  border-radius: 999px;
  border: 0.5px solid
    ${({ theme, $danger }) =>
      $danger
        ? theme.mode === 'dark'
          ? 'rgba(239,68,68,0.28)'
          : 'rgba(239,68,68,0.22)'
        : theme.mode === 'dark'
          ? 'rgba(255,255,255,0.12)'
          : 'rgba(0,0,0,0.08)'};
  background: ${({ theme }) =>
    theme.mode === 'dark'
      ? 'rgba(255,255,255,0.05)'
      : 'rgba(255,255,255,0.65)'};
  color: ${({ theme, $danger }) =>
    $danger ? theme.colors.error : theme.colors.textSecondary};
  font-size: 0.75rem;
  font-weight: 500;
  letter-spacing: -0.005em;
  cursor: pointer;
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  transition: all ${({ theme }) => theme.transitions.fast};

  svg {
    flex-shrink: 0;
  }

  &:hover {
    background: ${({ theme, $danger }) =>
      $danger
        ? theme.mode === 'dark'
          ? 'rgba(239,68,68,0.14)'
          : 'rgba(239,68,68,0.08)'
        : theme.mode === 'dark'
          ? 'rgba(255,255,255,0.09)'
          : 'rgba(255,255,255,0.92)'};
    color: ${({ theme, $danger }) =>
      $danger ? theme.colors.error : theme.colors.textPrimary};
    border-color: ${({ theme, $danger }) =>
      $danger
        ? theme.mode === 'dark'
          ? 'rgba(239,68,68,0.5)'
          : 'rgba(239,68,68,0.4)'
        : theme.mode === 'dark'
          ? 'rgba(255,255,255,0.2)'
          : 'rgba(0,0,0,0.14)'};
  }

  &:active {
    transform: translateY(0.5px);
  }
`;

/* ---- Skills Section ---- */

const SkillsSection: React.FC = () => {
  const { skills, isLoading, fetchSkills, deleteSkill } = useSkillStore();

  useEffect(() => { fetchSkills(); }, [fetchSkills]);

  if (isLoading) {
    return (
      <LoadingWrap>
        <LoadingLabel>스킬 로딩 중...</LoadingLabel>
      </LoadingWrap>
    );
  }

  if (skills.length === 0) {
    return (
      <SkillsEmptyWrap>
        <Sparkles size={32} />
        <SkillsEmptyText>업로드된 스킬이 없습니다.</SkillsEmptyText>
        <SkillsEmptyHint>
          채팅 입력창에서 Skill 모드를 선택하여 .md 또는 .zip 파일을 업로드하세요.
        </SkillsEmptyHint>
      </SkillsEmptyWrap>
    );
  }

  return (
    <SkillsListWrap>
      <SkillsHeader>
        <Sparkles size={16} />
        <span>내 스킬 ({skills.length})</span>
      </SkillsHeader>
      <SkillsGrid>
        {skills.map((sk) => (
          <SkillFileCard key={sk.skill_id}>
            <SkillFileIcon><FileText size={20} /></SkillFileIcon>
            <SkillFileInfo>
              <SkillFileName>{sk.name}</SkillFileName>
              {sk.description && <SkillFileDesc>{sk.description}</SkillFileDesc>}
              <SkillFileMeta>
                {sk.has_references && <Chip size="sm" variant="subtle">references</Chip>}
                {sk.has_assets && <Chip size="sm" variant="subtle">assets</Chip>}
                {sk.has_scripts && <Chip size="sm" variant="subtle">scripts</Chip>}
              </SkillFileMeta>
            </SkillFileInfo>
            <SkillDeleteBtn
              onClick={() => deleteSkill(sk.skill_id)}
              title="삭제"
            >
              <Trash2 size={14} />
            </SkillDeleteBtn>
          </SkillFileCard>
        ))}
      </SkillsGrid>
    </SkillsListWrap>
  );
};

const SkillsEmptyWrap = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  color: ${({ theme }) => theme.colors.textMuted};
`;

const SkillsEmptyText = styled.div`
  font-size: 0.875rem;
  font-weight: 500;
`;

const SkillsEmptyHint = styled.div`
  font-size: 0.75rem;
  opacity: 0.7;
  max-width: 320px;
  text-align: center;
`;

const SkillsListWrap = styled.div`
  flex: 1;
  padding: 16px 20px;
  overflow-y: auto;
`;

const SkillsHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 0.8125rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.textPrimary};
  margin-bottom: 12px;
`;

const SkillsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 8px;
`;

const SkillFileCard = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 12px 14px;
  border-radius: 10px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  transition: all ${({ theme }) => theme.transitions.fast};

  &:hover {
    background: ${({ theme }) => theme.colors.bgHover};
  }
`;

const SkillFileIcon = styled.div`
  flex-shrink: 0;
  width: 36px;
  height: 36px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${({ theme }) =>
    theme.mode === 'dark' ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)'};
  color: ${({ theme }) => theme.colors.textMuted};
`;

const SkillFileInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const SkillFileName = styled.div`
  font-size: 0.8125rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.textPrimary};
`;

const SkillFileDesc = styled.div`
  font-size: 0.75rem;
  color: ${({ theme }) => theme.colors.textMuted};
  margin-top: 2px;
`;

const SkillFileMeta = styled.div`
  display: flex;
  gap: 4px;
  margin-top: 6px;
  flex-wrap: wrap;
`;

const SkillDeleteBtn = styled.button`
  flex-shrink: 0;
  width: 28px;
  height: 28px;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.colors.textMuted};
  background: transparent;
  transition: all ${({ theme }) => theme.transitions.fast};

  &:hover {
    color: #ef4444;
    background: ${({ theme }) =>
      theme.mode === 'dark' ? 'rgba(239,68,68,0.15)' : 'rgba(239,68,68,0.08)'};
  }
`;
